import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Badge } from './ui/badge';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '-'; }
};

const formatCurrency = (val) => {
  if (val == null) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(val);
};

export function NettStatusDetailPopup({ detailPopup, nettStatusData, stock, onClose }) {
  if (!detailPopup || !nettStatusData) return null;

  return (
    <Dialog open={!!detailPopup} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{detailPopup?.title}</DialogTitle>
          <DialogDescription>{detailPopup?.description}</DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {/* Stock in Hand Details */}
          {detailPopup.type === 'stock_in_hand' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-muted-foreground">Total Stock</p>
                  <p className="text-2xl font-bold">{stock?.current?.stock_in_hand}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-muted-foreground">Stock Value</p>
                  <p className="text-2xl font-bold">{formatCurrency(stock?.current?.stock_value)}</p>
                </div>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  <TableRow><TableCell>Opening Stock</TableCell><TableCell className="text-right">{stock?.opening_stock?.quantity || 0}</TableCell><TableCell className="text-right">{formatCurrency(stock?.opening_stock?.value)}</TableCell></TableRow>
                  <TableRow><TableCell>Purchase</TableCell><TableCell className="text-right">{stock?.incoming?.purchase?.quantity || 0}</TableCell><TableCell className="text-right">{formatCurrency(stock?.incoming?.purchase?.value)}</TableCell></TableRow>
                  <TableRow><TableCell>Production</TableCell><TableCell className="text-right">{stock?.incoming?.production?.quantity || 0}</TableCell><TableCell className="text-right">{formatCurrency(stock?.incoming?.production?.value)}</TableCell></TableRow>
                  <TableRow><TableCell>BPD</TableCell><TableCell className="text-right">{stock?.incoming?.bpd?.quantity || 0}</TableCell><TableCell className="text-right">{formatCurrency(stock?.incoming?.bpd?.value)}</TableCell></TableRow>
                  <TableRow><TableCell>DC Inward</TableCell><TableCell className="text-right">{stock?.incoming?.dc_inward?.quantity || 0}</TableCell><TableCell className="text-right">{formatCurrency(stock?.incoming?.dc_inward?.value)}</TableCell></TableRow>
                  <TableRow className="border-t-2 font-bold"><TableCell>Less: Outgoing</TableCell><TableCell className="text-right text-red-600">-{stock?.outgoing?.total?.quantity || 0}</TableCell><TableCell className="text-right text-red-600">-{formatCurrency(stock?.outgoing?.total?.value)}</TableCell></TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {/* OHWIP */}
          {detailPopup.type === 'ohwip' && (
            <div>
              {nettStatusData.ohwip_details?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>WO Number</TableHead><TableHead>SO Number</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">WO Qty</TableHead><TableHead className="text-right">Min Issued</TableHead>
                  <TableHead className="text-right">OHWIP</TableHead><TableHead>Created</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.ohwip_details.map((wo, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{wo.work_order_number || '-'}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{wo.so_number || '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className="bg-yellow-100 text-yellow-800">{wo.status}</Badge></TableCell>
                      <TableCell className="text-right">{wo.quantity || 0}</TableCell>
                      <TableCell className="text-right">{wo.min_issued || 0}</TableCell>
                      <TableCell className="text-right font-bold text-yellow-600">{wo.ohwip_contribution || 0}</TableCell>
                      <TableCell>{formatDate(wo.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No Work Orders pending production issue</p>}
            </div>
          )}

          {/* WIP (Comp) */}
          {detailPopup.type === 'wip' && (
            <div>
              {nettStatusData.wip_comp_details?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>WO Number</TableHead><TableHead>SO Number</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">WO Qty</TableHead><TableHead className="text-right">WIP</TableHead><TableHead>Created</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.wip_comp_details.map((wo, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{wo.work_order_number || '-'}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{wo.so_number || '-'}</TableCell>
                      <TableCell><Badge variant="secondary" className="bg-purple-100 text-purple-800">{wo.status}</Badge></TableCell>
                      <TableCell className="text-right">{wo.quantity || 0}</TableCell>
                      <TableCell className="text-right font-bold text-purple-600">{wo.wip_contribution || 0}</TableCell>
                      <TableCell>{formatDate(wo.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No Work Orders currently in production</p>}
            </div>
          )}

          {/* WIP RMT */}
          {detailPopup.type === 'wip_rmt' && (
            <div>
              {nettStatusData.wip_rmt_details?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>JC Number</TableHead><TableHead>WO Number</TableHead>
                  <TableHead className="text-right">Quantity</TableHead><TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Value</TableHead><TableHead>Date</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.wip_rmt_details.map((entry, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{entry.jc_number || '-'}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{entry.work_order_number || '-'}</TableCell>
                      <TableCell className="text-right font-bold text-orange-600">{entry.quantity || 0}</TableCell>
                      <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(entry.value)}</TableCell>
                      <TableCell>{formatDate(entry.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No raw materials issued to production</p>}
            </div>
          )}

          {/* PO Issued */}
          {detailPopup.type === 'po_issued' && (
            <div>
              {nettStatusData.po_pending?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>PO Number</TableHead><TableHead>Supplier</TableHead>
                  <TableHead className="text-right">Qty Ordered</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.po_pending.map((po, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{po.po_number || '-'}</TableCell>
                      <TableCell>{po.supplier_name || '-'}</TableCell>
                      <TableCell className="text-right">{po.quantity || 0}</TableCell>
                      <TableCell className="text-right">{formatCurrency(po.unit_price)}</TableCell>
                      <TableCell><Badge variant="secondary">{po.status}</Badge></TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No pending Purchase Orders</p>}
            </div>
          )}

          {/* Indent Qty */}
          {detailPopup.type === 'indent_qty' && (
            <div>
              {nettStatusData.indent_pending?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>Indent Number</TableHead><TableHead>Requested By</TableHead>
                  <TableHead className="text-right">Requested</TableHead><TableHead className="text-right">Ordered</TableHead>
                  <TableHead className="text-right">Pending</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.indent_pending.map((indent, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{indent.indent_number || '-'}</TableCell>
                      <TableCell>{indent.requested_by || '-'}</TableCell>
                      <TableCell className="text-right">{indent.requested_qty || 0}</TableCell>
                      <TableCell className="text-right">{indent.issued_qty || 0}</TableCell>
                      <TableCell className="text-right font-medium text-teal-600">{indent.pending_qty || 0}</TableCell>
                      <TableCell><Badge variant="secondary">{indent.status}</Badge></TableCell>
                      <TableCell>{formatDate(indent.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No pending Indents</p>}
            </div>
          )}

          {/* Blocked — WO Material Demand (NEW FORMAT) */}
          {detailPopup.type === 'blocked' && (
            <div>
              {nettStatusData.blocked_details?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>WO Number</TableHead><TableHead>Parent Item</TableHead>
                  <TableHead className="text-right">Required</TableHead><TableHead className="text-right">Issued</TableHead>
                  <TableHead className="text-right">Pending</TableHead><TableHead>SO Number</TableHead><TableHead>Delivery</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.blocked_details.map((wo, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{wo.work_order_number || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={wo.parent_item_name}>{wo.parent_item_name || '-'}</TableCell>
                      <TableCell className="text-right">{wo.required_qty || 0}</TableCell>
                      <TableCell className="text-right">{wo.issued_qty || 0}</TableCell>
                      <TableCell className="text-right font-bold text-orange-600">{wo.pending_qty || 0}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{wo.so_number || '-'}</TableCell>
                      <TableCell>{formatDate(wo.delivery_date)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="border-t-2 font-bold bg-orange-50">
                    <TableCell colSpan={4}>Total Blocked</TableCell>
                    <TableCell className="text-right text-orange-700">
                      {nettStatusData.blocked_details.reduce((sum, r) => sum + (r.pending_qty || 0), 0)}
                    </TableCell>
                    <TableCell colSpan={2}></TableCell>
                  </TableRow>
                </TableBody></Table>
              ) : <p className="text-center py-8 text-muted-foreground">No material demand from Work Orders</p>}
            </div>
          )}

          {/* SO Blocked */}
          {detailPopup.type === 'so_blocked' && (
            <div>
              {nettStatusData.so_blocked_details?.length > 0 ? (
                <Table><TableHeader><TableRow>
                  <TableHead>SO Number</TableHead><TableHead>Client</TableHead><TableHead>Status</TableHead>
                  <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Dispatched</TableHead>
                  <TableHead className="text-right">Pending</TableHead><TableHead>Delivery</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {nettStatusData.so_blocked_details.map((so, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium font-mono">{so.so_number || '-'}</TableCell>
                      <TableCell>{so.client_name || '-'}</TableCell>
                      <TableCell><Badge variant="secondary">{so.status}</Badge></TableCell>
                      <TableCell className="text-right">{so.quantity || 0}</TableCell>
                      <TableCell className="text-right">{so.dispatched_quantity || 0}</TableCell>
                      <TableCell className="text-right font-bold text-red-600">{so.pending_quantity || 0}</TableCell>
                      <TableCell>{formatDate(so.delivery_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody></Table>
              ) : (
                <p className="text-center py-8 text-muted-foreground">
                  SO Blocked quantity: {stock?.current?.so_blocked || 0}
                  <br /><span className="text-sm">No detailed SO records found</span>
                </p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
